"use client";

import { galleryService } from "@/lib/services";
import { GalleryImage } from "@/lib/types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

// Skeleton Loader
function GallerySkeleton() {
	return (
		<div className="animate-pulse">
			<div className="relative mb-12">
				<div className="flex justify-center">
					<div className="relative w-full max-w-2xl h-96 rounded-xl bg-blue-200" />
				</div>
				<div className="flex justify-center gap-6 mt-8">
					<div className="w-12 h-12 rounded-full bg-blue-200" />
					<div className="w-12 h-12 rounded-full bg-blue-200" />
				</div>
			</div>
			<div className="max-w-2xl mx-auto space-y-4">
				<div className="h-4 bg-blue-200 rounded w-full" />
				<div className="h-4 bg-blue-200 rounded w-5/6" />
				<div className="h-4 bg-blue-200 rounded w-full" />
				<div className="h-4 bg-blue-200 rounded w-4/6" />
			</div>
		</div>
	);
}

// Empty State
function EmptyState() {
	return (
		<div className="text-center py-20">
			<div className="max-w-md mx-auto">
				<div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-12 h-12 text-blue-400"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-gray-900 mb-2">
					No Gallery Images Yet
				</h3>
				<p className="text-gray-600">
					Check back later to see photos from RFFA events and training sessions.
				</p>
			</div>
		</div>
	);
}

// Error State
function ErrorState({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="text-center py-20">
			<div className="max-w-md mx-auto">
				<div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-12 h-12 text-red-500"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-gray-900 mb-2">
					Oops! Something went wrong
				</h3>
				<p className="text-gray-600 mb-4">
					We couldn&apos;t load the gallery. Please try again.
				</p>
				<button
					onClick={onRetry}
					className="px-6 py-2 bg-primary hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
				>
					Try Again
				</button>
			</div>
		</div>
	);
}

export default function GalleryPageSection() {
	const [images, setImages] = useState<GalleryImage[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);

	const fetchGallery = async () => {
		setLoading(true);
		setError(false);

		try {
			const response = await galleryService.getAll();

			if (response.success && response.data) {
				setImages(response.data);
			} else {
				setError(true);
			}
		} catch (err) {
			console.error("Error fetching gallery:", err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchGallery();
	}, []);

	const nextSlide = () => {
		setCurrentIndex((prev) => (prev + 1 >= images.length ? 0 : prev + 1));
	};

	const prevSlide = () => {
		setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
	};

	// Get left, center, and right images
	const getDisplayImages = () => {
		if (images.length === 0) return { left: null, center: null, right: null };

		const leftIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
		const centerIndex = currentIndex;
		const rightIndex =
			currentIndex === images.length - 1 ? 0 : currentIndex + 1;

		return {
			left: images[leftIndex],
			center: images[centerIndex],
			right: images[rightIndex],
		};
	};

	// Loading State
	if (loading) {
		return (
			<section className="py-16 px-4 bg-blue-100">
				<div className="max-w-7xl mx-auto">
					<GallerySkeleton />
				</div>
			</section>
		);
	}

	// Error State
	if (error) {
		return (
			<section className="py-16 px-4 bg-blue-100">
				<div className="max-w-7xl mx-auto">
					<ErrorState onRetry={fetchGallery} />
				</div>
			</section>
		);
	}

	// Empty State
	if (images.length === 0) {
		return (
			<section className="py-16 px-4 bg-blue-100">
				<div className="max-w-7xl mx-auto">
					<EmptyState />
				</div>
			</section>
		);
	}

	const { left, center, right } = getDisplayImages();

	return (
		<section className="py-16 px-4 bg-blue-100">
			<div className="max-w-7xl mx-auto">
				{/* Images Container */}
				<div className="relative mb-7">
					{/* Mobile: Center image only, Desktop: Two column grid for left and right images with center overlay */}
					<div className="grid grid-cols-2 gap-5 mx-auto relative hidden md:grid">
						{/* Left Image - Desktop only */}
						{left && (
							<div className="relative h-64 md:h-72 lg:h-80 rounded-xl overflow-hidden shadow-md opacity-60 hover:opacity-75 transition-opacity">
								<Image
									src={left.image_url}
									alt={left.title || "Gallery image"}
									fill
									className="object-cover rounded-xl"
								/>
							</div>
						)}

						{/* Right Image - Desktop only */}
						{right && (
							<div className="relative h-64 md:h-72 lg:h-80 rounded-xl overflow-hidden shadow-md opacity-60 hover:opacity-75 transition-opacity">
								<Image
									src={right.image_url}
									alt={right.title || "Gallery image"}
									fill
									className="object-cover rounded-xl"
								/>
							</div>
						)}

						{/* Center Image - Absolute positioned on desktop */}
						{center && (
							<div className="absolute left-1/2 top-[10%] transform -translate-x-1/2 translate-y-12 z-10 w-4/5 lg:w-[55%]">
								<div className="relative h-74 md:h-82 lg:h-90 rounded-xl overflow-hidden shadow-lg">
									<Image
										src={center.image_url}
										alt={center.title || "Gallery image"}
										fill
										className="object-cover rounded-xl"
										priority
									/>
								</div>
							</div>
						)}
					</div>

					{/* Mobile: Center image only - Full width */}
					{center && (
						<div className="md:hidden">
							<div className="relative h-96 rounded-xl overflow-hidden shadow-lg">
								<Image
									src={center.image_url}
									alt={center.title || "Gallery image"}
									fill
									className="object-cover rounded-xl"
									priority
								/>
							</div>
						</div>
					)}

					{/* Navigation Arrows */}
					<div className="flex justify-center gap-6 mt-6 md:mt-40 relative z-20">
						<button
							onClick={prevSlide}
							className="w-12 h-12 rounded-full border-2 border-blue-300 flex items-center justify-center hover:border-primary hover:text-primary transition-colors bg-white/80 backdrop-blur-sm"
							aria-label="Previous image"
						>
							<ChevronLeft className="w-6 h-6" />
						</button>
						<button
							onClick={nextSlide}
							className="w-12 h-12 rounded-full border-2 border-blue-300 flex items-center justify-center hover:border-primary hover:text-primary transition-colors bg-white/80 backdrop-blur-sm"
							aria-label="Next image"
						>
							<ChevronRight className="w-6 h-6" />
						</button>
					</div>

					{/* Image Counter */}
					<div className="text-center mt-6 text-gray-600 text-sm">
						{currentIndex + 1} of {images.length}
					</div>
				</div>

				{/* Description/Details - Bold and prominent - Only for center image */}
				{center?.description && (
					<div className="max-w-2xl mx-auto text-center">
						{center.title && (
							<h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
								{center.title}
							</h2>
						)}
						<p className="text-gray-600 leading-relaxed text-base md:text-lg">
							{center.description}
						</p>
					</div>
				)}
			</div>
		</section>
	);
}
